import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { HTMLAttributes, ComponentType, ReactNode } from 'react';
import { BaseText, IconCircleInfo, IconColoredWarning, IconLoadingDonuts, IconProps, Stack } from '../../components';
import { Typography, semantic_colors } from '../../foundation';

export type StateKind = 'warning' | 'empty' | 'loading';
export interface StateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  kind?: StateKind;
  /**
   * default: 20
   */
  size?: 20 | 32 | 40;
  title?: ReactNode;
  description: ReactNode;
  icon?: ComponentType<IconProps>;
  action?: ReactNode;
}

const stateKindIcon: { [key in StateKind]: ComponentType } = {
  warning: IconColoredWarning,
  empty: IconCircleInfo,
  loading: IconLoadingDonuts,
} as const;

export const State = ({ kind, size = 20, title, description, action, icon, className, ...props }: StateProps) => {
  const Icon = icon ?? (kind ? (stateKindIcon[kind] as ComponentType<IconProps>) : undefined);
  const desc_typography: Typography = kind === 'warning' ? 'Body_15_SemiBold' : 'Body_14_Medium';

  return (
    <Stack direction='column' gap={8} align='center' className={classNames('pds-state', className)} {...props}>
      {Icon && <Icon aria-hidden size={size} color={semantic_colors.content.tertiary} />}
      {title && (
        <StyledText kind='Heading_24_Bold' color={semantic_colors.content.primary}>
          {title}
        </StyledText>
      )}
      <Stack direction='column' gap={16} align='center'>
        {description && (
          <StyledText kind={desc_typography} color={semantic_colors.content.tertiary}>
            {description}
          </StyledText>
        )}
        {action && action}
      </Stack>
    </Stack>
  );
};

const StyledText = styled(BaseText)`
  white-space: pre-line;
  text-align: center;
`;
